import ErrorUtil, {
    BusinessDataType,
    COMMON_ERROR,
    ErrorHandleType,
    ErrorNetInfo
} from "@/utils/service/error";
import { HttpMethod } from "@/enums/net";
import Axios from "./Axios";
// import { useAppStore } from "@/store/modules/app";
import { showLoadingToast } from "vant";

// const axios = Axios();

export type Config = {
    /**
     * 错误处理函数
     */
    errors?: string | ((res: BusinessDataType) => void) | boolean;
    /**
     * 是否显示 loading，默认为 false
     */
    showLoading?: boolean;
    /**
     * 是否显示 token，默认为 true
     */
    needToken?: boolean;
    /**
     * 超时时间
     */
    timeout?: number;
};

export type RequestConfig<T = object> = {
    /**
     * 请求接口
     */
    url: string;
    /**
     * 请求参数，不需要像 axios 中区分 get 和 post，底层有做处理
     */
    params?: T;
    /**
     * 底层有针对 post 和 json 类型做处理
     */
    headers?: object;
    /**
     * 请求配置
     */
    option?: Omit<Config, "errors">;
    /**
     * 请求方法
     */
    method?: HttpMethod;
    /**
     * 错误处理方法
     * true: 不阻止函数后续运行
     * false: 阻止函数后续运行
     * string: 阻止函数后续运行并简单弹出 toast
     * function: 阻止函数后续运行并执行传入的函数
     */
    errors?: string | ((res: BusinessDataType) => void) | boolean;
};

type RequestOptionType = { url: string; headers: object; method: HttpMethod; data: object };

export default class HttpUtil {

    static getAxios() {
        return Axios();
    }

    /**
     * 判断是否将这次请求计入埋点
     * @returns
     */
    static getLog() {
        return false;
    }

    /**
     * 获取Http请求的scheme, http:// 或者 https://
     * @return String scheme
     */
    static getScheme() {
        return "http://";
    }

    /**
     * 统一发送get请求方法
     */
    static async get(payload: RequestConfig) {
        return await this.request({ ...payload, method: HttpMethod.GET });
    }

    /**
     * 统一发送post请求方法
     */
    static async post(payload: RequestConfig) {
        return await this.request({ ...payload, method: HttpMethod.POST });
    }

    /**
     * 统一发送json请求方法
     */
    static async json(payload: RequestConfig) {
        return await this.request({ ...payload, method: HttpMethod.JSON });
    }

    /**
     * 通用发送http请求的方法
     * @param  {String} path    请求路径
     * @param  {object} params  请求参数
     * @param  {object} headers 请求首部
     * @param  {object} options fetch.api 其他可配置信息
     * @return {object}         调用结果
     */
    static async call(
        path: string,
        params = {},
        config: Config = {},
        headers: { [a: string]: string } = {},
        method: HttpMethod = HttpMethod.GET
    ) {
        const { timeout = 20 * 1000, showLoading = true, needToken = true } = config;
        return this.request({
            url: path,
            params,
            headers,
            method,
            errors: config.errors,
            option: {
                showLoading,
                timeout,
                needToken
            }
        });
    }

    static async request(config: RequestConfig): Promise<any> {
        try {
            this.checkRequest(config);
            const url = this.getUrl(config);
            const headers = this.getHeader(config);
            const method = this.getMethod(config);
            const data = this.getParamsDefault(config);
            const errors = config.errors as ErrorHandleType;
            const showLoading = config.option?.showLoading || false;

            const requestOptions: RequestOptionType = { url, data, method, headers };

            const request = this.makeRequest(requestOptions, errors);
            const timeout = this.makeTimeout(config);
            const loading = showLoading && showLoadingToast("加载中...");
            return await Promise.race([request, timeout]).finally(() => {
                if (showLoading && loading) loading.close();
            });
        } catch (error: unknown) {
            ErrorUtil.handleNet(error as ErrorNetInfo);
        }
    }

    private static async makeRequest(
        requestOptions: RequestOptionType,
        errors: ErrorNetInfo["handle"]
    ): Promise<object> {
        const result = await this.getAxios()(requestOptions);

        if (!(result && result.status === 200)) {
            const error: ErrorNetInfo = {
                type: "Network",
                handle: COMMON_ERROR,
                data: result
            };
            throw error;
        }

        const data = result.data as BusinessDataType;
        if (!(data && data.code === 10000)) {
            const error: ErrorNetInfo = {
                type: "Business",
                handle: errors,
                data
            };
            throw error;
        }

        return data.data;
    }

    /**
     * 生成超时时间的Promise
     * @param  {String|Number} timeout 超时时间
     * @return {Promise} 超时Promise
     */
    private static async makeTimeout(config: RequestConfig): Promise<object> {
        return await new Promise((_, rj) =>
            setTimeout(
                () => {
                    const error: ErrorNetInfo = {
                        type: "Timeout",
                        handle: config.errors as ErrorHandleType,
                        data: {}
                    };
                    rj(error);
                },
                config.option?.timeout || 5 * 1000
            )
        );
    }

    /**
     * 获取请求路径
     * @param path
     * @returns
     */
    private static getUrl(config: RequestConfig): string {
        return config.url;
    }

    /**
     * 获取请求头
     * @param config
     */
    private static getHeader(config: RequestConfig): object {
        let headers = config.headers || {};
        const { needToken } = { ...getDefaultOption()!, ...config.option };
        const method = this.getMethod(config);
        // 添加 token
        // if (needToken && useAppStore().token) {
        //     headers = { ...headers, token: useAppStore().token };
        // }

        // 添加 application/json
        if ([HttpMethod.POST, HttpMethod.JSON].includes(method)) {
            headers = { ...headers, "Content-Type": "application/json" };
        }

        return headers;
    }

    /**
     * 获取请求参数
     * @param config
     */
    private static getParamsDefault(config: RequestConfig): object {
        const method = this.getMethod(config);
        let params: object = config.params || {};

        // if (method === HttpMethod.POST) {
        //     params = JSON.stringify(params);
        // }

        // if (method === HttpMethod.JSON) {
        //     params = JSON.stringify({ ...params, t_: Date.now() });
        // }

        if (method === HttpMethod.GET && typeof params === "object") {
            params = { ...params, t_: Date.now() };
        }

        return params;
    }

    private static getMethod(config: RequestConfig): HttpMethod {
        return (config.method?.toUpperCase() as HttpMethod) || HttpMethod.GET;
    }

    /**
     * 校验请求是否合理
     */
    private static checkRequest(config: RequestConfig): boolean {
        // 如果需要 token 有没有 token 在
        const { needToken } = { ...getDefaultOption()!, ...config.option };
        // if (needToken && !useAppStore().token) {
        //     const error: ErrorNetInfo = {
        //         type: "Business",
        //         handle: false,
        //         data: {}
        //     };
        //     throw error;
        // }
        return true;
    }
}

function getDefaultOption(): Required<RequestConfig["option"]> {
    return {
        needToken: true,
        showLoading: false,
        timeout: 20 * 1000
    };
}
